import React, { type ReactNode, type ButtonHTMLAttributes } from 'react'
import { cn } from '../utils'

const BTN_BASE = 'inline-flex items-center justify-center gap-1.5 font-medium rounded-lg transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed select-none whitespace-nowrap active:scale-[0.98]'

const BTN_VARIANTS = {
    ghost:   'bg-white dark:bg-white/5 text-gray-600 dark:text-gray-300 border border-black/[.07] dark:border-white/[.07] hover:bg-gray-50 dark:hover:bg-white/10',
    primary: 'bg-blue-600 text-white hover:bg-blue-700 border border-transparent shadow-sm hover:shadow',
    danger:  'bg-red-500/10 text-red-600 dark:text-red-400 border border-red-200/60 dark:border-red-500/20 hover:bg-red-500/15',
    success: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200/60 dark:border-emerald-500/20 hover:bg-emerald-500/15',
    warning: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-200/60 dark:border-amber-500/20 hover:bg-amber-500/15',
    outline: 'bg-transparent text-gray-600 dark:text-gray-400 border border-black/[.15] dark:border-white/[.15] hover:bg-gray-50 dark:hover:bg-white/5',
}

const BTN_SIZES = {
    xs: 'text-[10px] px-2 py-1',
    sm: 'text-[11px] px-2.5 py-1.5',
    md: 'text-[13px] px-3 py-2',
    lg: 'text-[14px] px-4 py-2.5',
}

interface BtnProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: keyof typeof BTN_VARIANTS
    size?: keyof typeof BTN_SIZES
    loading?: boolean
    icon?: ReactNode
}

export const Button = React.forwardRef<HTMLButtonElement, BtnProps>(
    ({ variant = 'ghost', size = 'md', loading, icon, children, className, disabled, ...rest }, ref) => (
        <button
            ref={ref}
            disabled={disabled || loading}
            className={cn(BTN_BASE, BTN_VARIANTS[variant], BTN_SIZES[size], className)}
            {...rest}
        >
            {loading ? (
                <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                </svg>
            ) : icon}
            {children && <span>{children}</span>}
        </button>
    )
)
Button.displayName = 'Button'